import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  RotateCw, Loader2, LogIn, LogOut, UserPlus, UserX, AlertCircle, Mail, Settings,
  CheckCircle2, XCircle, Trash2, Activity, TrendingUp, Target, GitBranch,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * /admin/activity-log — live member-activity portal.
 *
 * Surfaces every login/signup/abandon event captured by the
 * `services/activity_alerts.py` pipeline, plus controls to tune
 * the alert recipient and abandonment-threshold.
 */
const EVENT_ICONS = {
  login_success:    LogIn,
  login_failed:     LogOut,
  signup_started:   UserPlus,
  signup_completed: CheckCircle2,
  signup_abandoned: UserX,
  signup_failed:    AlertCircle,
};

const EVENT_PALETTES = {
  login_success:    "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  signup_completed: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  signup_started:   "bg-gold/10 text-gold border-gold/30",
  login_failed:     "bg-red-500/10 text-red-300 border-red-500/30",
  signup_abandoned: "bg-red-500/10 text-red-300 border-red-500/30",
  signup_failed:    "bg-red-500/10 text-red-300 border-red-500/30",
};

const formatLocal = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ActivityLogPage = () => {
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [dropOff, setDropOff] = useState(null);
  const [events, setEvents] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterHours, setFilterHours] = useState("168"); // 7d default
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [reconcileBusy, setReconcileBusy] = useState(false);

  const fetchAll = useCallback(async (showToast = false) => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.set("event_type", filterType);
      if (filterEmail.trim()) params.set("email", filterEmail.trim());
      if (filterHours) params.set("since_hours", filterHours);
      params.set("limit", "200");

      const [eventsR, statsR, settingsR, funnelR, dropOffR] = await Promise.all([
        api.get(`/admin/activity-log?${params.toString()}`),
        api.get("/admin/activity-log/stats"),
        api.get("/admin/activity-log/settings"),
        api.get("/admin/activity-log/funnel?weeks=8"),
        api.get("/admin/activity-log/drop-off-insights?days=30"),
      ]);
      setEvents(eventsR.data.events || []);
      setStats(statsR.data);
      setSettings(settingsR.data);
      setFunnel(funnelR.data);
      setDropOff(dropOffR.data);
      if (showToast) toast.success("Activity log refreshed.");
    } catch (e) {
      console.error("activity log fetch failed", e);
      toast.error(e.response?.data?.detail || "Could not load activity log.");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [filterType, filterEmail, filterHours]);

  useEffect(() => { fetchAll(false); }, [fetchAll]);

  const saveSettings = async () => {
    if (!settings) return;
    setSettingsBusy(true);
    try {
      await api.put("/admin/activity-log/settings", {
        recipient:       settings.recipient,
        alerts_enabled:  settings.alerts_enabled,
        abandon_minutes: Number(settings.abandon_minutes) || 60,
      });
      toast.success("Alert settings saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setSettingsBusy(false);
    }
  };

  const runReconcile = async () => {
    setReconcileBusy(true);
    try {
      const r = await api.post("/admin/activity-log/reconcile-abandoned");
      toast.success(`Reconciliation complete — ${r.data.flagged} new abandoned signup(s).`);
      fetchAll(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reconcile failed.");
    } finally {
      setReconcileBusy(false);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this activity event permanently?")) return;
    try {
      await api.delete(`/admin/activity-log/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast.success("Event deleted.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed.");
    }
  };

  const statRows = stats ? Object.entries(stats.event_types).map(([key, meta]) => ({
    key,
    label: meta.label,
    last24h: stats.last_24h?.[key] || 0,
    last7d: stats.last_7d?.[key] || 0,
    all: stats.all_time?.[key] || 0,
  })) : [];

  return (
    <DashboardLayout
      title="Member Activity Log"
      subtitle="Live audit trail of every login, signup, and abandoned checkout — plus the email-alert pipeline."
    >
      <div className="space-y-6" data-testid="activity-log-page">
        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {statRows.map((r) => {
            const Icon = EVENT_ICONS[r.key] || Activity;
            const palette = EVENT_PALETTES[r.key] || "bg-platinum/10 text-platinum/80 border-platinum/20";
            return (
              <div
                key={r.key}
                className={`rounded-lg border ${palette} p-4 flex flex-col gap-2`}
                data-testid={`activity-stat-${r.key}`}
              >
                <div className="flex items-center gap-2">
                  <Icon size={14} />
                  <p className="text-[10px] uppercase tracking-wider font-semibold">{r.label}</p>
                </div>
                <p className="text-2xl font-serif">{r.last24h}</p>
                <p className="text-[10px] opacity-70">24h · 7d: {r.last7d} · all: {r.all}</p>
              </div>
            );
          })}
        </div>

        {/* Signup funnel conversion widget */}
        {funnel && (
          <Card className="border-platinum/10 bg-navy-light/30" data-testid="funnel-widget">
            <CardHeader>
              <CardTitle className="text-platinum text-base font-serif flex items-center gap-2">
                <GitBranch size={16} className="text-gold" /> Signup Funnel Conversion
              </CardTitle>
              <CardDescription className="text-platinum/60">
                How many members who open Stripe checkout actually complete and get assigned a VC#.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Hero conversion rates */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { label: "Last 7 days",  data: funnel.last_7d,  icon: TrendingUp },
                  { label: "Last 30 days", data: funnel.last_30d, icon: Target },
                  { label: `Last ${funnel.window.weeks} weeks`, data: funnel.window, icon: Activity },
                ].map((bucket) => {
                  const conv = bucket.data.conversion_pct;
                  const tone = conv >= 70 ? "emerald" : conv >= 40 ? "gold" : "red";
                  const palette = {
                    emerald: { ring: "border-emerald-500/30", bg: "bg-emerald-500/[0.06]", text: "text-emerald-300" },
                    gold:    { ring: "border-gold/30",        bg: "bg-gold/[0.06]",         text: "text-gold" },
                    red:     { ring: "border-red-500/30",     bg: "bg-red-500/[0.06]",      text: "text-red-300" },
                  }[tone];
                  const Icon = bucket.icon;
                  return (
                    <div
                      key={bucket.label}
                      className={`rounded-lg border ${palette.ring} ${palette.bg} p-5`}
                      data-testid={`funnel-bucket-${bucket.label.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Icon size={14} className={palette.text} />
                        <p className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${palette.text}`}>
                          {bucket.label}
                        </p>
                      </div>
                      <p className={`text-4xl font-serif ${palette.text}`}>
                        {conv}<span className="text-2xl opacity-70">%</span>
                      </p>
                      <p className="text-platinum/55 text-[11px] mt-3">
                        <span className="text-platinum/80 font-semibold">{bucket.data.completed}</span> completed of{" "}
                        <span className="text-platinum/80 font-semibold">{bucket.data.started}</span> started
                        {bucket.data.abandoned > 0 && (
                          <> · <span className="text-red-300/80">{bucket.data.abandoned} abandoned</span></>
                        )}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Weekly trend bar chart */}
              {funnel.weekly && funnel.weekly.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-platinum/55 font-semibold mb-3">
                    Weekly trend (started · completed · conversion %)
                  </p>
                  <div className="flex items-end gap-2 px-1" data-testid="funnel-weekly-chart">
                    {(() => {
                      const BAR_AREA_PX = 140;
                      const maxStarted = Math.max(1, ...funnel.weekly.map((w) => w.started));
                      return funnel.weekly.map((w) => {
                        const startedPx = (w.started / maxStarted) * BAR_AREA_PX;
                        const completedPx = w.started ? (w.completed / w.started) * startedPx : 0;
                        const conv = w.conversion_pct;
                        const convTone = conv >= 70 ? "text-emerald-300" : conv >= 40 ? "text-gold" : "text-red-300";
                        return (
                          <div
                            key={w.iso_week}
                            className="flex-1 flex flex-col items-center gap-1.5 group cursor-default min-w-0"
                            title={`${w.iso_week}: ${w.completed}/${w.started} = ${conv}%`}
                          >
                            <span className={`text-[10px] font-semibold ${convTone}`}>{conv}%</span>
                            <div
                              className="w-full relative bg-platinum/[0.04] border border-platinum/10 rounded-t-sm"
                              style={{ height: `${BAR_AREA_PX}px` }}
                            >
                              {/* total-started bar (faint) */}
                              <div
                                className="absolute inset-x-0 bottom-0 bg-gold/20 rounded-t-sm"
                                style={{ height: `${startedPx}px` }}
                              />
                              {/* completed bar (solid gold) */}
                              <div
                                className="absolute inset-x-0 bottom-0 bg-gold/80 transition-all group-hover:bg-gold rounded-t-sm"
                                style={{ height: `${completedPx}px` }}
                              />
                            </div>
                            <span className="text-[9px] text-platinum/50 font-mono">W{w.iso_week.split("-W")[1]}</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                  <div className="flex items-center justify-end gap-4 mt-3 text-[10px] text-platinum/55">
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 bg-gold/20 rounded-sm" /> Started
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 bg-gold/80 rounded-sm" /> Completed
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Drop-off insights — surfaces auto-detected abandonment patterns */}
        {dropOff && dropOff.abandoned > 0 && (
          <Card
            className={`border-2 ${
              dropOff.severity === "high"   ? "border-red-500/40 bg-red-500/[0.04]" :
              dropOff.severity === "medium" ? "border-gold/40 bg-gold/[0.04]" :
                                              "border-platinum/10 bg-navy-light/30"
            }`}
            data-testid="dropoff-insights"
          >
            <CardHeader>
              <CardTitle className="text-platinum text-base font-serif flex items-center gap-2">
                <AlertCircle
                  size={16}
                  className={
                    dropOff.severity === "high"   ? "text-red-300" :
                    dropOff.severity === "medium" ? "text-gold" :
                                                    "text-platinum/60"
                  }
                />
                Drop-off Insights · last {dropOff.days} days
              </CardTitle>
              <CardDescription className="text-platinum/70">
                {dropOff.summary}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Quick metric row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center rounded-md border border-platinum/10 bg-obsidian/40 p-3" data-testid="dropoff-metric-started">
                  <p className="text-[10px] uppercase tracking-wider text-platinum/55">Started</p>
                  <p className="text-2xl font-serif text-platinum">{dropOff.started}</p>
                </div>
                <div className="text-center rounded-md border border-emerald-500/20 bg-emerald-500/[0.04] p-3" data-testid="dropoff-metric-completed">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-300">Completed</p>
                  <p className="text-2xl font-serif text-emerald-300">{dropOff.completed}</p>
                </div>
                <div className="text-center rounded-md border border-red-500/20 bg-red-500/[0.04] p-3" data-testid="dropoff-metric-abandoned">
                  <p className="text-[10px] uppercase tracking-wider text-red-300">Abandoned ({dropOff.abandoned_pct}%)</p>
                  <p className="text-2xl font-serif text-red-300">{dropOff.abandoned}</p>
                </div>
              </div>

              {/* Insights list */}
              {dropOff.insights.length === 0 ? (
                <p className="text-platinum/55 italic text-sm py-3 text-center">
                  No standout patterns detected — abandonment looks evenly distributed.
                </p>
              ) : (
                <div className="space-y-2">
                  {dropOff.insights.map((ins) => (
                    <div
                      key={`${ins.kind}-${ins.label}`}
                      className="rounded-md border border-platinum/10 bg-obsidian/30 p-3 flex items-start gap-3"
                      data-testid={`dropoff-insight-${ins.kind}`}
                    >
                      <div className="shrink-0 w-7 h-7 rounded-full bg-gold/15 text-gold flex items-center justify-center text-xs font-bold">
                        {ins.weight}
                      </div>
                      <div className="min-w-0">
                        <p className="text-platinum font-semibold text-sm">{ins.label}</p>
                        <p className="text-platinum/65 text-xs leading-relaxed mt-1">{ins.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Alert settings */}
        <Card className="border-platinum/10 bg-navy-light/30">
          <CardHeader>
            <CardTitle className="text-platinum text-base font-serif flex items-center gap-2">
              <Mail size={16} className="text-gold" /> Email Alert Settings
            </CardTitle>
            <CardDescription className="text-platinum/60">
              {settings?.forwarding_note}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {settings && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="alert-recipient" className="text-platinum/80 text-xs uppercase tracking-wider">
                      Alert recipient
                    </Label>
                    <Input
                      id="alert-recipient"
                      data-testid="alert-recipient-input"
                      value={settings.recipient}
                      onChange={(e) => setSettings({ ...settings, recipient: e.target.value })}
                      className="bg-obsidian/50 border-platinum/20 text-platinum"
                    />
                  </div>
                  <div>
                    <Label htmlFor="abandon-min" className="text-platinum/80 text-xs uppercase tracking-wider">
                      Abandon after (minutes)
                    </Label>
                    <Input
                      id="abandon-min"
                      type="number"
                      min={5}
                      max={10080}
                      data-testid="alert-abandon-input"
                      value={settings.abandon_minutes}
                      onChange={(e) => setSettings({ ...settings, abandon_minutes: e.target.value })}
                      className="bg-obsidian/50 border-platinum/20 text-platinum"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={settings.alerts_enabled}
                      onCheckedChange={(v) => setSettings({ ...settings, alerts_enabled: v })}
                      data-testid="alert-enabled-switch"
                    />
                    <span className="text-platinum/80 text-sm">Email alerts enabled</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={runReconcile}
                      disabled={reconcileBusy}
                      variant="outline"
                      className="border-platinum/30 text-platinum hover:bg-platinum/10"
                      data-testid="reconcile-btn"
                    >
                      {reconcileBusy
                        ? <Loader2 size={14} className="mr-2 animate-spin" />
                        : <Settings size={14} className="mr-2" />}
                      Re-check abandoned
                    </Button>
                    <Button
                      onClick={saveSettings}
                      disabled={settingsBusy}
                      className="bg-gold hover:bg-gold-light text-navy-dark font-semibold"
                      data-testid="save-settings-btn"
                    >
                      {settingsBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                      Save settings
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="border-platinum/10 bg-navy-light/30">
          <CardContent className="p-5 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-platinum/80 text-xs uppercase tracking-wider">Event type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="bg-obsidian/50 border-platinum/20 text-platinum" data-testid="filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-platinum/20 text-platinum">
                  <SelectItem value="all">All events</SelectItem>
                  {stats && Object.entries(stats.event_types).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-platinum/80 text-xs uppercase tracking-wider">Email contains</Label>
              <Input
                value={filterEmail}
                onChange={(e) => setFilterEmail(e.target.value)}
                placeholder="member@email.com"
                className="bg-obsidian/50 border-platinum/20 text-platinum"
                data-testid="filter-email"
              />
            </div>
            <div>
              <Label className="text-platinum/80 text-xs uppercase tracking-wider">Window</Label>
              <Select value={filterHours} onValueChange={setFilterHours}>
                <SelectTrigger className="bg-obsidian/50 border-platinum/20 text-platinum" data-testid="filter-hours">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-platinum/20 text-platinum">
                  <SelectItem value="1">Last 1 hour</SelectItem>
                  <SelectItem value="24">Last 24 hours</SelectItem>
                  <SelectItem value="168">Last 7 days</SelectItem>
                  <SelectItem value="720">Last 30 days</SelectItem>
                  <SelectItem value="0">All time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => fetchAll(true)}
              disabled={refreshing}
              className="bg-gold hover:bg-gold-light text-navy-dark font-semibold"
              data-testid="refresh-btn"
            >
              {refreshing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RotateCw size={14} className="mr-2" />}
              Refresh
            </Button>
          </CardContent>
        </Card>

        {/* Events table */}
        <Card className="border-platinum/10 bg-navy-light/30">
          <CardHeader>
            <CardTitle className="text-platinum text-base font-serif flex items-center gap-2">
              <Activity size={16} className="text-gold" />
              Events ({events.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center text-platinum/60 py-10">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : events.length === 0 ? (
              <p className="text-platinum/50 italic text-sm py-6 text-center">No events match the current filters.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-platinum/55">
                    <tr className="border-b border-platinum/10">
                      <th className="text-left py-2 px-2">Event</th>
                      <th className="text-left py-2 px-2">Email</th>
                      <th className="text-left py-2 px-2">Name</th>
                      <th className="text-left py-2 px-2">VC# / Code</th>
                      <th className="text-left py-2 px-2">IP</th>
                      <th className="text-left py-2 px-2">When</th>
                      <th className="text-left py-2 px-2">Alert</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => {
                      const palette = EVENT_PALETTES[e.event_type] || "bg-platinum/10 text-platinum/80 border-platinum/20";
                      return (
                        <tr
                          key={e.id}
                          className="border-b border-platinum/5 hover:bg-platinum/[0.02]"
                          data-testid={`activity-row-${e.id}`}
                        >
                          <td className="py-2 px-2">
                            <Badge className={`${palette} border text-[10px]`}>
                              {(stats?.event_types?.[e.event_type]?.label || e.event_type)}
                            </Badge>
                          </td>
                          <td className="py-2 px-2 text-platinum/90 font-mono text-xs">{e.email || "—"}</td>
                          <td className="py-2 px-2 text-platinum/80">{e.full_name || "—"}</td>
                          <td className="py-2 px-2 text-gold font-mono text-xs">{e.vc_number || e.referral_code || "—"}</td>
                          <td className="py-2 px-2 text-platinum/60 font-mono text-xs">{e.ip || "—"}</td>
                          <td className="py-2 px-2 text-platinum/70 text-xs">{formatLocal(e.created_at)}</td>
                          <td className="py-2 px-2">
                            {e.alert_sent
                              ? <CheckCircle2 size={14} className="text-emerald-400" />
                              : <XCircle size={14} className="text-platinum/30" />}
                          </td>
                          <td className="py-2 px-2">
                            <button
                              onClick={() => deleteEvent(e.id)}
                              className="text-platinum/40 hover:text-red-400 transition-colors"
                              data-testid={`activity-delete-${e.id}`}
                              title="Delete event"
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ActivityLogPage;
